import {Center, HStack, forwardRef} from '@chakra-ui/react';
import {ChakraDiv, ChakraSpan} from '@linx-security/ui/utils/chakra-elements';
import type {GroupBase, OptionBase, SelectComponent, SelectComponentsConfig} from 'chakra-react-select';
import {chakraComponents, Select, createFilter} from 'chakra-react-select';
import {FixedSizeList} from 'react-window';
import type {ReactNode} from 'react';
import {Children} from 'react';

type CustomSelectComponent = SelectComponent;

export interface SystemOption extends OptionBase {
  image?: ReactNode;
  value: string;
  label: string;
}

const components: SelectComponentsConfig<SystemOption, false, GroupBase<SystemOption>> = {
  MultiValueContainer: ({children, ...props}) => {
    return (
      <chakraComponents.MultiValueContainer
        {...props}
        sx={{...props.sx, backgroundColor: `blackAlpha.200`, alignItems: `center`, px: `8`}}>
        {children}
      </chakraComponents.MultiValueContainer>
    );
  },
  MultiValueLabel: ({children, ...props}) => {
    return (
      <chakraComponents.MultiValueLabel {...props}>
        <HStack>
          <Center>{props.data.image}</Center>
          <ChakraDiv>{children}</ChakraDiv>
        </HStack>
      </chakraComponents.MultiValueLabel>
    );
  },
  Option: ({children, ...props}) => {
    return (
      <chakraComponents.Option {...props}>
        <HStack>
          {props.data.image}
          <ChakraSpan>{children}</ChakraSpan>
        </HStack>
      </chakraComponents.Option>
    );
  },
  MenuList: ({children, ...props}) => {
    const {options, maxHeight, getValue} = props;

    const [value] = getValue();

    const itemSize = 35;

    const initialOffset = options.indexOf(value) * itemSize;

    const height = options.length * itemSize > maxHeight ? maxHeight : options.length * itemSize;

    return (
      <FixedSizeList
        height={height}
        itemCount={Children.count(children)}
        width={`100%`}
        itemSize={35}
        initialScrollOffset={initialOffset}>
        {({index, style}) => (
          <div style={{...style, backgroundColor: `white`}}>
            {(children?.[index as keyof typeof children] ?? ``) as ReactNode}
          </div>
        )}
      </FixedSizeList>
    );
  },
};

export const SystemSelect: CustomSelectComponent = forwardRef((props, ref) => (
  <Select
    {...props}
    ref={ref}
    filterOption={props.filterOption ?? createFilter({ignoreAccents: false})}
    // @ts-expect-error chakra-react-select types not matching but functioning correctly
    components={props.components ?? components}
  />
));
